import React, { useState, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import Breadcrums from "../../../common/breadcrumbs";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import NoRowsOverlay from "../../../common/NoRowsOverlay";
import * as ClubManagamentApi from "../../../services/club.service";
import * as clubAdmin from "../../../services/clubAdmin.service";
import Swal from "sweetalert2";
import { handleServerValidations, formateDateWithMonth } from "../../../../../utils/commonfunction";
import { SWAL_SETTINGS, THEME_COLORS } from "../../../../../utils/Constants";
import { Placeholder, Whisper, Popover } from "rsuite";

ModuleRegistry.registerModules([AllCommunityModule]);

// Manual "Charge Current Month" / "Charge Next Month" actions are hidden; billing runs via the monthly cron
const SHOW_MANUAL_CHARGE_BUTTONS = false;

// "2026-12" → "25 Nov 26 - 1 Dec 26" (reminder on the 25th of the previous month, charged on the 1st)
const formatBillingPeriod = (billingPeriod) => {
  const [year, month] = String(billingPeriod || "").split("-").map(Number);
  if (!year || !month) return billingPeriod || "N/A";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const fmt = (d) => `${d.getDate()} ${months[d.getMonth()]} ${String(d.getFullYear()).slice(-2)}`;
  return `${fmt(new Date(year, month - 2, 25))} - ${fmt(new Date(year, month - 1, 1))}`;
};

const BillingSnapshot = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const gridRef = useRef();
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: t("Club Management"), url: "/admin/club-management/club/list/1" },
    { title: "Billing Snapshots", url: "" },
  ];

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["billing-snapshots", id],
    queryFn: () => ClubManagamentApi.getBillingSnapshots(id),
    refetchOnWindowFocus: false,
    retry: false,
    onError: (error) => {
      Swal.fire({
        icon: "error",
        text: handleServerValidations(error),
        ...SWAL_SETTINGS,
      });
    },
  });

  const colDefs = useMemo(() => [
    { 
      headerName: "ID", 
      field: "id", 
      minWidth: 150, 
      maxWidth: 250, 
      tooltipField: "id",
      cellRenderer: (params) => (
        <div style={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {params.value || "N/A"}
        </div>
      )
    },
    { headerName: "Billing Period", valueGetter: params => formatBillingPeriod(params.data.billingPeriod), minWidth: 200 },
    { headerName: "Month/Year", valueGetter: params => `${params.data.month || "N/A"}/${params.data.year || "N/A"}`, minWidth: 120 },
    { 
      headerName: "Player Count", 
      field: "playerCount", 
      minWidth: 140,
      cellRenderer: (params) => {
        const count = params.value || 0;
        const players = params.data?.playerDetails || [];
        const speaker = (
          <Popover title="Player Details" style={{ maxWidth: 300, minWidth: 220 }}>
            {players.length > 0 ? (
              <div style={{ maxHeight: 200, overflowY: "auto" }}>
                {players.map((p, idx) => (
                  <div key={idx} style={{ padding: "6px 0", borderBottom: idx < players.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: "#0f172a" }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: "#64748b" }}>{p.email}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: "#64748b", padding: "4px 0" }}>No player details available</div>
            )}
          </Popover>
        );

        return (
          <Whisper trigger={["click", "hover"]} placement="autoVerticalStart" speaker={speaker}>
            <span
              className="badge badge-primary-light text-primary fw-bold"
              style={{ cursor: "pointer", fontSize: "13px", padding: "4px 10px", display: "inline-flex", alignItems: "center", gap: "4px" }}
              title="Click to view players"
            >
              {count} <i className="ri-information-line" style={{ fontSize: "14px" }}></i>
            </span>
          </Whisper>
        );
      }
    },
    { 
      headerName: "Player Additions", 
      field: "additionalPlayersCount", 
      minWidth: 150,
      cellRenderer: (params) => {
        const count = params.value !== undefined && params.value !== null ? params.value : 0;
        const players = params.data?.additionalPlayerDetails || [];
        const speaker = (
          <Popover title="Additional Player Details" style={{ maxWidth: 300, minWidth: 220 }}>
            {players.length > 0 ? (
              <div style={{ maxHeight: 200, overflowY: "auto" }}>
                {players.map((p, idx) => (
                  <div key={idx} style={{ padding: "6px 0", borderBottom: idx < players.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: "#0f172a" }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: "#64748b" }}>{p.email}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: "#64748b", padding: "4px 0" }}>No additional player details available</div>
            )}
          </Popover>
        );

        return (
          <Whisper trigger={["click", "hover"]} placement="autoVerticalStart" speaker={speaker}>
            <span
              className={`badge ${count > 0 ? "badge-info-light text-primary fw-bold" : "badge-light text-muted"}`}
              style={{ cursor: "pointer", fontSize: "13px", padding: "4px 10px", display: "inline-flex", alignItems: "center", gap: "4px" }}
              title="Click to view additional players"
            >
              {count} {count > 0 && <i className="ri-information-line" style={{ fontSize: "14px" }}></i>}
            </span>
          </Whisper>
        );
      }
    },
    { headerName: "Adjustment Amount", field: "additionalAmount", valueFormatter: params => params.value ? `£${(params.value/100).toFixed(2)}` : "£0.00", minWidth: 150 },
    { headerName: "Amount", field: "amount", valueFormatter: params => params.value ? `£${(params.value/100).toFixed(2)}` : "£0.00", minWidth: 120 },
    { headerName: "Platform Fee", field: "platformFee", valueFormatter: params => params.value ? `£${(params.value/100).toFixed(2)}` : "£0.00", minWidth: 120 },
    { headerName: "Total Amount", field: "totalAmount", valueFormatter: params => params.value ? `£${(params.value/100).toFixed(2)}` : "£0.00", minWidth: 120 },
    { headerName: "Status", field: "status", minWidth: 120, cellRenderer: params => {
      const val = params.value ? params.value.toLowerCase() : "";
      let badgeClass = "badge-primary-light";
      if (val === "paid" || val === "free_period") {
        badgeClass = "badge-success-light";
      } else if (val === "failed") {
        badgeClass = "badge-danger-light";
      } else if (val === "invoiced") {
        badgeClass = "badge-info-light";
      } else if (val === "draft" || val === "pending") {
        badgeClass = "badge-warning-light text-dark";
      }
      return <span className={`badge badge-pill ${badgeClass} fw-bold text-uppercase`}>{params.value || "N/A"}</span>;
    }},
    { 
      headerName: "Invoice ID", 
      field: "stripeInvoiceId", 
      minWidth: 150, 
      maxWidth: 200,
      tooltipField: "stripeInvoiceId",
      cellRenderer: (params) => (
        <div style={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {params.value || "N/A"}
        </div>
      )
    },
    {
      headerName: "Created Date",
      field: "createdAt",
      minWidth: 180,
      valueFormatter: params => params.value ? formateDateWithMonth(params.value) : "N/A"
    }
  ], []);

  const rowData = useMemo(() => {
    const rawData = data?.data;
    let list = Array.isArray(rawData) ? rawData : (rawData?.snapshots || []);
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(item => 
        (item.stripeInvoiceId && item.stripeInvoiceId.toLowerCase().includes(s)) ||
        (item.billingPeriod && item.billingPeriod.toLowerCase().includes(s)) ||
        formatBillingPeriod(item.billingPeriod).toLowerCase().includes(s) ||
        (item.id && item.id.toLowerCase().includes(s))
      );
    }
    if (statusFilter && statusFilter !== "all") {
      list = list.filter(item => item.status && item.status.toLowerCase() === statusFilter.toLowerCase());
    }
    return list;
  }, [data, search, statusFilter]);

  const clubInfo = useMemo(() => {
    const rawData = data?.data;
    if (Array.isArray(rawData)) {
      const first = rawData[0];
      return {
        clubType: first?.clubType || "Standard",
        ratePerPlayer: first?.rateApplied || 5,
        totalChargedAmount: rawData.reduce((acc, s) => acc + (s.totalAmount || 0), 0),
        unchargedPlayersCount: 0,
        additionsBillingStatus: "Fully Billed"
      };
    }
    return {
      clubType: rawData?.clubType || "Standard",
      ratePerPlayer: rawData?.ratePerPlayer || 5,
      totalChargedAmount: rawData?.totalChargedAmount || 0,
      activePlayersCount: rawData?.activePlayersCount || 0,
      unchargedPlayersCount: rawData?.unchargedPlayersCount || 0,
      additionsBillingStatus: rawData?.additionsBillingStatus || "Fully Billed"
    };
  }, [data]);

  const getAgGridConfig = useMemo(() => ({
    ref: gridRef,
    domLayout: "autoHeight",
    rowData: rowData,
    columnDefs: colDefs,
    noRowsOverlayComponent: NoRowsOverlay,
    pagination: true,
    paginationPageSize: perPage,
    paginationPageSizeSelector: [10, 20, 50, 100],
    defaultColDef: {
      resizable: true,
      sortable: true,
      minWidth: 150,
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
    },
    headerHeight: 50,
    rowHeight: 45,
    onPaginationChanged: (params) => {
      if (params.api) {
        setPerPage(params.api.paginationGetPageSize());
      }
    },
  }), [rowData, colDefs, perPage]);

  const handleChargeCurrentMonth = () => {
    Swal.fire({
      customClass: "swal-wide",
      title: "Charge Current Month Billing?",
      text: "Are you sure you want to generate billing snapshot and charge the bill for current month?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: "Yes, Charge Bill",
      cancelButtonText: t("btn_cancel")
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Processing Billing...",
          text: "Generating snapshot and processing payment via Stripe",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        clubAdmin.chargeBilling(id).then((response) => {
          if (response.success) {
            Swal.fire({
              icon: "success",
              title: "Billing Charged!",
              text: response.message || "Snapshot generated successfully.",
              ...SWAL_SETTINGS,
            });
            refetch();
            setTimeout(() => refetch(), 1200);
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        }).catch((err) => {
          Swal.fire({ icon: "error", text: handleServerValidations(err), ...SWAL_SETTINGS });
        });
      }
    });
  };

  const handleChargeNextMonth = () => {
    Swal.fire({
      customClass: "swal-wide",
      title: "Charge Next Month Billing?",
      text: "Are you sure you want to generate billing snapshot and charge the bill for Next Month?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: "Yes, Charge Next Month",
      cancelButtonText: t("btn_cancel")
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Processing Next Month Billing...",
          text: "Generating next month snapshot and processing payment via Stripe",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        clubAdmin.chargeNextMonthBilling(id).then((response) => {
          if (response.success) {
            Swal.fire({
              icon: "success",
              title: "Next Month Billing Charged!",
              text: response.message || "Next month snapshot generated successfully.",
              ...SWAL_SETTINGS,
            });
            refetch();
            setTimeout(() => refetch(), 1200);
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        }).catch((err) => {
          Swal.fire({ icon: "error", text: handleServerValidations(err), ...SWAL_SETTINGS });
        });
      }
    });
  };

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <div className="animation_fade">
        <div className="card custom-card overflow-hidden">
          <div className="card-body">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
              <div>
                <h6 className="main-content-label mb-2">Billing Snapshots</h6>
                <div className="d-flex flex-wrap align-items-center gap-3">
                  <div className="d-flex align-items-center gap-1 fs-13">
                    <span className="text-muted fw-semibold">Club Type:</span>
                    <span className="badge badge-pill badge-primary-light text-uppercase fw-bold ms-1">
                      {clubInfo.clubType}
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-1 fs-13">
                    <span className="text-muted fw-semibold">Price per Player:</span>
                    <span className="fw-bold text-dark ms-1">
                      £{Number(clubInfo.ratePerPlayer || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-1 fs-13">
                    <span className="text-muted fw-semibold">Total Charged Amount:</span>
                    <span className="fw-bold text-success ms-1">
                      £{(Number(clubInfo.totalChargedAmount || 0) / 100).toFixed(2)}
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-1 fs-13">
                    <span className="text-muted fw-semibold">Uncharged Additions:</span>
                    <span className={`badge ${clubInfo.unchargedPlayersCount > 0 ? "badge-warning-light text-dark fw-bold" : "badge-success-light fw-bold"}`}>
                      {clubInfo.unchargedPlayersCount} Players
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-1 fs-13">
                    <span className="text-muted fw-semibold">Billing Status:</span>
                    <span className={`badge badge-pill ${clubInfo.unchargedPlayersCount > 0 ? "badge-info-light text-primary" : "badge-success-light text-success"} fw-bold`}>
                      {clubInfo.additionsBillingStatus}
                    </span>
                  </div>
                </div>
              </div>
              <div className="d-flex flex-wrap align-items-center gap-2">
                {SHOW_MANUAL_CHARGE_BUTTONS && (
                  <>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                      onClick={handleChargeCurrentMonth}
                    >
                      <i className="ri-bank-card-line"></i> Charge Current Month
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-success d-flex align-items-center gap-1"
                      style={{ backgroundColor: "#059669", borderColor: "#059669" }}
                      onClick={handleChargeNextMonth}
                    >
                      <i className="ri-calendar-check-line"></i> Charge Next Month
                    </button>
                  </>
                )}
                <div style={{ width: 180 }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search Invoice ID, Period"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ outline: "none", boxShadow: "none" }}
                  />
                </div>
                <div style={{ width: 140 }}>
                  <select
                    className="form-select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{ outline: "none", boxShadow: "none" }}
                  >
                    <option value="all">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="draft">Draft</option>
                    <option value="invoiced">Invoiced</option>
                    <option value="paid">Paid</option>
                    <option value="failed">Failed</option>
                    <option value="free_period">Free Period</option>
                  </select>
                </div>
              </div>
            </div>
            {isLoading ? (
              <Placeholder.Paragraph className="w-100 admin-placeholder" rowHeight={30} rowSpacing={5} active={true} rows={7} />
            ) : (
              <div className="ag-theme-alpine" style={{ width: '100%' }}>
                <AgGridReact {...getAgGridConfig} />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BillingSnapshot;
